<script lang="ts">
  import { onMount } from 'svelte'
  import { api, type Target, type Category, type IpGroup, type HappRuleSet } from '../lib/api'
  import { fmtRelative, copyToClipboard } from '../lib/utils'
  import { notify } from '../lib/toast'
  import Modal from '../components/Modal.svelte'

  let targets = $state<Target[]>([])
  let cats = $state<Category[]>([])
  let ipGroups = $state<IpGroup[]>([])
  let ruleSets = $state<HappRuleSet[]>([])

  let addOpen = $state(false), editOpen = $state(false)
  let editing = $state<Target | null>(null)

  let newSlug = $state(''), newName = $state(''), newType = $state<'mikrotik'|'happ'|'plain'|'remnawave'>('mikrotik'), newDesc = $state('')

  let editName = $state(''), editDesc = $state(''), editEnabled = $state(true)
  let editForwardTo = $state('')
  let editCategoryIds = $state<Set<number>>(new Set())
  let editIpGroupIds = $state<Set<number>>(new Set())
  let editRuleSetIds = $state<Set<number>>(new Set())

  async function load() {
    [targets, cats, ipGroups, ruleSets] = await Promise.all([
      api.listTargets(), api.listCategories(), api.listIpGroups(), api.listHappRuleSets()
    ])
  }
  onMount(load)

  const typeLabels: Record<string, string> = {
    mikrotik: 'MikroTik',
    happ: 'Happ',
    plain: 'Plain',
    remnawave: 'Remnawave'
  }

  const artifactsByType: Record<string, string[]> = {
    mikrotik: ['mikrotik.rsc', 'vless-domains.txt', 'vless-ipranges.txt'],
    happ: ['happ-profile.json', 'happ-base64.txt', 'remnawave-routing.txt'],
    plain: ['vless-domains.txt', 'vless-ipranges.txt'],
    remnawave: ['remnawave-routing.txt', 'happ-base64.txt']
  }

  async function handleAdd() {
    if (!newSlug || !newName) return notify('Slug и название обязательны', 'err')
    try {
      await api.createTarget({ slug: newSlug, name: newName, type: newType, description: newDesc || undefined })
      notify('Эндпоинт создан', 'ok')
      addOpen = false; newSlug = ''; newName = ''; newDesc = ''
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  function openEdit(t: Target) {
    editing = t
    editName = t.name; editDesc = t.description || ''; editEnabled = t.enabled
    let cfgOv: any = {}
    try { cfgOv = t.configJson ? JSON.parse(t.configJson) : {} } catch {}
    editForwardTo = cfgOv.mikrotik_forward_to || ''
    editCategoryIds = new Set(t.categories.filter(c => c.enabled).map(c => c.categoryId))
    editIpGroupIds = new Set((t.ipGroups || []).filter(g => g.enabled).map(g => g.ipGroupId))
    editRuleSetIds = new Set((t.happRuleSets || []).filter(r => r.enabled).map(r => r.ruleSetId))
    editOpen = true
  }

  async function handleEdit() {
    if (!editing) return
    const fwd = editForwardTo.trim()
    if (fwd && !/^(\d{1,3}\.){3}\d{1,3}(\s*,\s*(\d{1,3}\.){3}\d{1,3})*$/.test(fwd)) {
      return notify('forward-to: укажите IP (или несколько через запятую)', 'err')
    }
    try {
      const payload: any = {
        name: editName, description: editDesc, enabled: editEnabled,
        categoryIds: [...editCategoryIds]
      }
      if (editing.type === 'mikrotik') {
        let cfgOv: any = {}
        try { cfgOv = editing.configJson ? JSON.parse(editing.configJson) : {} } catch {}
        if (fwd) cfgOv.mikrotik_forward_to = fwd
        else delete cfgOv.mikrotik_forward_to
        payload.configJson = JSON.stringify(cfgOv)
      }
      if (['mikrotik', 'plain'].includes(editing.type)) payload.ipGroupIds = [...editIpGroupIds]
      if (['happ', 'remnawave'].includes(editing.type)) payload.ruleSetIds = [...editRuleSetIds]
      await api.updateTarget(editing.id, payload)
      notify('Сохранено', 'ok'); editOpen = false; await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function regenToken(t: Target) {
    if (!confirm('Перегенерировать токен? Старые URL перестанут работать.')) return
    try {
      const r = await api.regenTargetToken(t.id)
      notify(`Новый токен: ${r.token.slice(0, 12)}...`, 'ok')
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function deleteTarget(t: Target) {
    if (!confirm(`Удалить эндпоинт ${t.slug}?`)) return
    try { await api.deleteTarget(t.id); notify('Удалено', 'ok'); await load() }
    catch (e: any) { notify(e.message, 'err') }
  }

  async function copy(text: string) {
    if (await copyToClipboard(text)) notify('Скопировано', 'ok')
  }

  function toggleSet(set: Set<number>, id: number, checked: boolean) {
    if (checked) set.add(id); else set.delete(id)
    return new Set(set)
  }
</script>

<div class="card mb-4">
  <div class="flex justify-between items-center">
    <h2 class="text-base font-semibold">Эндпоинты</h2>
    <button class="btn btn-primary" onclick={() => (addOpen = true)}>+ Эндпоинт</button>
  </div>
  <p class="text-xs text-muted mt-2">
    Каждый эндпоинт — отдельный URL с уникальным токеном. MikroTik-эндпоинты получают домены + IP-группы. Happ-эндпоинты получают домены + наборы правил.
  </p>
</div>

<div class="grid md:grid-cols-2 gap-4">
  {#each targets as t (t.id)}
    {@const base = location.origin + '/t/' + t.token}
    {@const files = artifactsByType[t.type] || []}
    <div class="card">
      <div class="flex justify-between items-start gap-2 mb-2 flex-wrap">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <h3 class="font-semibold">{t.name}</h3>
            <span class="badge badge-blue">{typeLabels[t.type] || t.type}</span>
            {#if t.enabled}<span class="badge badge-green">активен</span>{:else}<span class="badge badge-red">отключён</span>{/if}
          </div>
          <div class="text-xs text-muted mt-1 break-all">
            slug: <code>{t.slug}</code> ·
            запрошено <b>{t.fetchCount}</b>×
            {#if t.lastFetchAt}· {fmtRelative(t.lastFetchAt)}{/if}
          </div>
        </div>
        <div class="flex gap-1 flex-shrink-0">
          <button class="btn-icon" onclick={() => openEdit(t)} title="Редактировать">✎</button>
          <button class="btn-icon" onclick={() => regenToken(t)} title="Сменить токен">🔄</button>
          <button class="btn-icon btn-icon-danger" onclick={() => deleteTarget(t)} title="Удалить">🗑</button>
        </div>
      </div>
      {#if t.description}<p class="text-xs text-muted mb-2">{t.description}</p>{/if}

      <div class="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-bg-border text-center">
        <div>
          <div class="text-[10px] text-muted uppercase">Категорий</div>
          <div class="text-sm font-semibold">{t.categories.filter(c => c.enabled).length} / {cats.length}</div>
        </div>
        {#if ['mikrotik', 'plain'].includes(t.type)}
          <div>
            <div class="text-[10px] text-muted uppercase">IP групп</div>
            <div class="text-sm font-semibold">{(t.ipGroups || []).filter(g => g.enabled).length} / {ipGroups.length}</div>
          </div>
        {/if}
        {#if ['happ', 'remnawave'].includes(t.type)}
          <div>
            <div class="text-[10px] text-muted uppercase">Happ наборов</div>
            <div class="text-sm font-semibold">{(t.happRuleSets || []).filter(r => r.enabled).length} / {ruleSets.length}</div>
          </div>
        {/if}
      </div>

      <div class="mt-3 pt-3 border-t border-bg-border">
        <div class="label">Публичные URL (без авторизации)</div>
        <div class="flex flex-col gap-1.5">
          {#each files as f}
            {@const url = `${base}/${f}`}
            <div class="flex items-center gap-1 bg-bg-sunk rounded border border-bg-border px-2 py-1">
              <code class="text-[11px] font-mono text-blue-300 flex-1 min-w-0 truncate" title={url}>{f}</code>
              <button class="btn-icon" onclick={() => copy(url)} title="Копировать">⧉</button>
              <a class="btn-icon" href={url} target="_blank" title="Открыть">↗</a>
            </div>
          {/each}
        </div>
      </div>
    </div>
  {/each}
</div>

{#if addOpen}
  <Modal title="Новый эндпоинт" onclose={() => (addOpen = false)}>
    <div class="space-y-3">
      <div><label class="label">Slug (уникальный)</label><input class="input" bind:value={newSlug} placeholder="mikrotik-home" autofocus></div>
      <div><label class="label">Название</label><input class="input" bind:value={newName} placeholder="MikroTik домашний"></div>
      <div><label class="label">Тип</label>
        <select class="input" bind:value={newType}>
          <option value="mikrotik">MikroTik</option>
          <option value="happ">Happ</option>
          <option value="plain">Plain list</option>
          <option value="remnawave">Remnawave</option>
        </select>
      </div>
      <div><label class="label">Описание (опц.)</label><input class="input" bind:value={newDesc}></div>
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => (addOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleAdd}>Создать</button>
    {/snippet}
  </Modal>
{/if}

{#if editOpen && editing}
  <Modal title="Редактировать эндпоинт" onclose={() => (editOpen = false)} wide>
    <div class="space-y-4">
      <div><label class="label">Название</label><input class="input" bind:value={editName}></div>
      <div><label class="label">Описание</label><input class="input" bind:value={editDesc}></div>
      <label class="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" class="checkbox" bind:checked={editEnabled}>
        <span class="text-sm">Активен</span>
      </label>

      {#if editing.type === 'mikrotik'}
        <div>
          <label class="label">DNS forward-to — IP резолвера обхода</label>
          <input class="input" bind:value={editForwardTo} placeholder="192.168.0.59">
          <p class="text-xs text-muted mt-1">Подставляется в FWD-записи mikrotik.rsc. Пусто — используется глобальная настройка «MikroTik DNS forward-to»; если и она пуста, записи выводятся без forward-to и не резолвятся.</p>
        </div>
      {/if}

      <div>
        <label class="label">Категории доменов</label>
        <div class="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
          {#each cats as c}
            <label class="flex items-center gap-2 cursor-pointer p-2 rounded border border-bg-border bg-bg-sunk hover:border-accent/50">
              <input type="checkbox" class="checkbox" checked={editCategoryIds.has(c.id)}
                     onchange={(e) => editCategoryIds = toggleSet(editCategoryIds, c.id, (e.target as HTMLInputElement).checked)}>
              <span class="text-xs flex-1">{c.name}</span>
              <span class="text-[10px] text-muted">{c._count?.domains || 0}</span>
            </label>
          {/each}
        </div>
      </div>

      {#if ['mikrotik', 'plain'].includes(editing.type)}
        <div>
          <label class="label">IP группы (для MikroTik)</label>
          <div class="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
            {#each ipGroups as g}
              <label class="flex items-center gap-2 cursor-pointer p-2 rounded border border-bg-border bg-bg-sunk hover:border-accent/50">
                <input type="checkbox" class="checkbox" checked={editIpGroupIds.has(g.id)}
                       onchange={(e) => editIpGroupIds = toggleSet(editIpGroupIds, g.id, (e.target as HTMLInputElement).checked)}>
                <span class="text-xs flex-1">{g.name}</span>
                <span class="text-[10px] text-muted">{g._count?.ipRanges || 0}</span>
              </label>
            {/each}
          </div>
        </div>
      {/if}

      {#if ['happ', 'remnawave'].includes(editing.type)}
        <div>
          <label class="label">Наборы правил Happ</label>
          <div class="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
            {#each ruleSets as rs}
              <label class="flex items-center gap-2 cursor-pointer p-2 rounded border border-bg-border bg-bg-sunk hover:border-accent/50">
                <input type="checkbox" class="checkbox" checked={editRuleSetIds.has(rs.id)}
                       onchange={(e) => editRuleSetIds = toggleSet(editRuleSetIds, rs.id, (e.target as HTMLInputElement).checked)}>
                <span class="text-xs flex-1">{rs.name}</span>
                <span class="text-[10px] text-muted">{rs._count?.tags || 0}</span>
              </label>
            {/each}
          </div>
        </div>
      {/if}
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => (editOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleEdit}>Сохранить</button>
    {/snippet}
  </Modal>
{/if}
